import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "../../../../api/client";
import { usePermission } from "../../../../hooks/usePermission";
import { PERMISSIONS } from "../../../../constants/permissions";
import useAuth from "../../../../hooks/useAuth";
import Toast from "../../components/Toast";
import LogMovementModal, { REASONS, TypeBadge } from "./LogMovementModal";
import LockableButton from "../../components/LockableButton";
import { verifyModuleEnabled } from "../../utils/moduleAccess";
import "./StockMovements.css";

const inputStyle = {
  width: "100%", padding: "8px 12px", border: "1px solid #d6dfed",
  borderRadius: 8, fontSize: 14, boxSizing: "border-box",
};

function downloadCSV(rows, filename) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const csv = [
    headers.join(","),
    ...rows.map((row) => headers.map((h) => JSON.stringify(row[h] ?? "")).join(",")),
  ].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function StockMovements() {
  const canManageInventory = usePermission(PERMISSIONS.MANUFACTURING_INVENTORY_MANAGE);
  const { enabledModules, setEnabledModules } = useAuth();
  const hasInventoryModule = !enabledModules || enabledModules.includes("inventory");
  const [searchParams, setSearchParams] = useSearchParams();
  const [movements, setMovements] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [partFilter, setPartFilter] = useState(searchParams.get("partMasterId") || "");
  const [typeFilter, setTypeFilter] = useState("");

  const handlePartFilterChange = (value) => {
    setPartFilter(value);
    setSearchParams(value ? { partMasterId: value } : {}, { replace: true });
  };
  const [modal, setModal] = useState(false);
  const [toast, setToast] = useState(null);
  const pageRef = useRef(1);
  const fetchingRef = useRef(false);
  const sentinelRef = useRef(null);

  const handleLogMovementClick = async () => {
    const { allowed, message } = await verifyModuleEnabled("inventory", { enabledModules, setEnabledModules });
    if (!allowed) {
      setToast({ msg: message, type: "error" });
      return;
    }
    setModal(true);
  };

  useEffect(() => {
    api.get("/manufacturing/parts").then((r) => setParts(r.data.data || [])).catch(() => {});
  }, []);

  const fetchMovements = useCallback(async ({ page, reset }) => {
    if (fetchingRef.current) return;
    fetchingRef.current = true;
    if (reset) setLoading(true); else setLoadingMore(true);
    try {
      const params = { page, limit: 20 };
      if (partFilter) params.partMasterId = partFilter;
      if (typeFilter) params.type = typeFilter;
      const res = await api.get("/manufacturing/inventory/movements", { params });
      const { data = [], hasMore: more = false } = res.data;
      setMovements((prev) => reset ? data : [...prev, ...data]);
      setHasMore(more);
      pageRef.current = page;
    } catch {
      if (reset) setMovements([]);
    } finally {
      if (reset) setLoading(false); else setLoadingMore(false);
      fetchingRef.current = false;
    }
  }, [partFilter, typeFilter]);

  useEffect(() => { fetchMovements({ page: 1, reset: true }); }, [fetchMovements]);

  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const observer = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting && hasMore && !fetchingRef.current) {
        fetchMovements({ page: pageRef.current + 1, reset: false });
      }
    }, { rootMargin: "200px" });
    observer.observe(el);
    return () => observer.disconnect();
  }, [hasMore, fetchMovements]);

  const handleExport = async () => {
    try {
      const params = {};
      if (partFilter) params.partMasterId = partFilter;
      if (typeFilter) params.type = typeFilter;
      const res = await api.get("/manufacturing/inventory/movements/export", { params });
      const rows = (res.data.data || []).map((m) => ({
        Date: new Date(m.createdAt).toLocaleString("en-IN"),
        Part: m.partMaster?.name,
        Code: m.partMaster?.code,
        Type: m.type.toUpperCase(),
        Quantity: m.quantity,
        "Unit Price": m.unitPrice ?? "",
        Reason: m.reason,
        Reference: m.referenceNo ?? "",
        "Logged By": m.loggedBy ? `${m.loggedBy.firstName} ${m.loggedBy.lastName}` : "",
        Note: m.note ?? "",
      }));
      downloadCSV(rows, `stock-movements-${new Date().toISOString().split("T")[0]}.csv`);
    } catch {
      setToast({ msg: "Failed to export", type: "error" });
    }
  };

  return (
    <div style={{ padding: 20, height: "100%", overflow: "auto", background: "#f5f8fc" }}>
      <Link className="back-link" to="/manufacturing/inventory">← Back to Inventory</Link>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", margin: "12px 0 20px", flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ margin: "0 0 4px", color: "#0f2d5c" }}>Stock Movements</h1>
          <p style={{ margin: 0, color: "#64748b", fontSize: 14 }}>
            {partFilter
              ? `Movements for ${parts.find((p) => p.id === partFilter)?.name || "selected part"}.`
              : "Full inflow / outflow ledger for all parts."}
          </p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button className="btn-secondary" onClick={handleExport}>Export CSV</button>
          {canManageInventory && (
            <LockableButton
              className="btn-primary"
              onClick={handleLogMovementClick}
              locked={!hasInventoryModule}
              lockedTitle="Inventory module is not enabled for your company"
            >
              + Log Movement
            </LockableButton>
          )}
        </div>
      </div>

      <div style={{ background: "#fff", border: "1px solid #e4ebf5", borderRadius: 12, overflow: "hidden" }}>
        <div className="sm-toolbar">
          <div className="sm-filters">
            <select style={{ ...inputStyle, width: 200 }} value={partFilter} onChange={(e) => handlePartFilterChange(e.target.value)}>
              <option value="">All parts</option>
              {parts.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
            <select style={{ ...inputStyle, width: 140 }} value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
              <option value="">All types</option>
              <option value="in">IN</option>
              <option value="out">OUT</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>Loading…</div>
        ) : movements.length === 0 ? (
          <div style={{ padding: 40, textAlign: "center", color: "#94a3b8" }}>No movements logged yet.</div>
        ) : (
          <>
            <div className="sm-table-wrap">
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr style={{ background: "#f8fafc" }}>
                    {["Date", "Part", "Type", "Qty", "Price", "Reason", "Reference", "Note", "Logged By"].map((h) => (
                      <th key={h} style={{ padding: "10px 16px", textAlign: "left", fontWeight: 700, color: "#374151", borderBottom: "1px solid #e4ebf5", whiteSpace: "nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {movements.map((m) => (
                    <tr key={m.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ padding: "10px 16px", color: "#64748b", whiteSpace: "nowrap" }}>{new Date(m.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                      <td style={{ padding: "10px 16px", color: "#0f2d5c", fontWeight: 600 }}>{m.partMaster?.name}</td>
                      <td style={{ padding: "10px 16px" }}><TypeBadge type={m.type} /></td>
                      <td style={{ padding: "10px 16px", fontWeight: 700, color: m.type === "in" ? "#15803d" : "#dc2626" }}>
                        {m.type === "in" ? "+" : "-"}{m.quantity}
                      </td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{m.unitPrice != null ? `₹${m.unitPrice}` : "—"}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{REASONS.find((r) => r.value === m.reason)?.label || m.reason}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{m.referenceNo || "—"}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={m.note || ""}>{m.note || "—"}</td>
                      <td style={{ padding: "10px 16px", color: "#64748b" }}>{m.loggedBy ? `${m.loggedBy.firstName} ${m.loggedBy.lastName}` : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sm-cards">
              {movements.map((m) => (
                <div key={m.id} className="sm-card">
                  <div className="sm-card-top">
                    <span style={{ fontWeight: 700, color: "#0f2d5c" }}>{m.partMaster?.name}</span>
                    <TypeBadge type={m.type} />
                  </div>
                  <div className="sm-card-meta">
                    <strong style={{ color: m.type === "in" ? "#15803d" : "#dc2626" }}>{m.type === "in" ? "+" : "-"}{m.quantity}</strong>
                    {" · "}{new Date(m.createdAt).toLocaleString("en-IN", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </div>
                  <div className="sm-card-meta">by {m.loggedBy ? `${m.loggedBy.firstName} ${m.loggedBy.lastName}` : "—"}</div>
                  {m.note && <div className="sm-card-meta" style={{ fontStyle: "italic" }}>“{m.note}”</div>}
                </div>
              ))}
            </div>

            <div ref={sentinelRef} style={{ height: 1 }} />
            {loadingMore && <div style={{ padding: "16px 0", textAlign: "center", color: "#94a3b8", fontSize: 13 }}>Loading more…</div>}
          </>
        )}
      </div>

      {modal && (
        <LogMovementModal
          parts={parts}
          onClose={() => setModal(false)}
          onSaved={() => { setModal(false); fetchMovements({ page: 1, reset: true }); setToast({ msg: "Movement logged", type: "success" }); }}
        />
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onDone={() => setToast(null)} />}
    </div>
  );
}
